import { StreamChat, UserResponse, OwnUserResponse } from 'stream-chat';
import type { StreamChatUser } from '../types/chat.types';
import config from '../../../config.frontend';

/**
 * StreamChatService - Core service for managing Stream Chat client
 * Handles connection, disconnection, and user session management
 */
class StreamChatService {
  private static instance: StreamChatService;
  private client: StreamChat | null = null;
  private currentUser: OwnUserResponse | null = null;
  private connectionPromise: Promise<void> | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance of StreamChatService
   */
  public static getInstance(): StreamChatService {
    if (!StreamChatService.instance) {
      StreamChatService.instance = new StreamChatService();
    }
    return StreamChatService.instance;
  }

  /**
   * Initialize Stream Chat client
   * @param apiKey - Stream API key from environment
   */
  private initializeClient(apiKey: string): StreamChat {
    if (!this.client) {
      this.client = StreamChat.getInstance(apiKey);
      console.log('[StreamChatService] Client initialized');
    }
    return this.client;
  }

  /**
   * Connect user to Stream Chat using token provider pattern
   * This follows Stream's recommended authentication approach
   *
   * @param userId - Unique user ID (from Firebase/backend)
   * @param tokenOrProvider - JWT token string or token provider function
   * @param userData - Additional user data (name, image, etc.)
   */
  public async connectUser(
    userId: string,
    tokenOrProvider: string | (() => Promise<string>),
    userData?: Partial<StreamChatUser>
  ): Promise<OwnUserResponse> {
    try {
      // Get API key from config
      const apiKey = config.streamChatApiKey;
      if (!apiKey) {
        throw new Error('Stream API key not configured');
      }

      // Initialize client if needed
      const client = this.initializeClient(apiKey);

      // Prevent duplicate connections
      if (this.connectionPromise) {
        console.log('[StreamChatService] Connection already in progress');
        await this.connectionPromise;
        return this.currentUser!;
      }

      // Create connection promise
      this.connectionPromise = (async () => {
        // Disconnect existing user if any
        if (this.currentUser) {
          await this.disconnectUser();
        }

        console.log('userData', userData);
        // Connect new user with token or token provider
        // If userData is not provided, just pass the userId - the backend has already
        // created the user with all necessary fields (display_name, email, role, region_id)
        await client.connectUser(
          userData ? {
            id: userId,
            ...userData,
          } : {
            id: userId,
          },
          tokenOrProvider
        );

        this.currentUser = client.user as OwnUserResponse;
        console.log('[StreamChatService] User connected:', client.user?.id);
      })();

      await this.connectionPromise;
      this.connectionPromise = null;

      return this.currentUser!;
    } catch (error) {
      this.connectionPromise = null;
      console.error('[StreamChatService] Failed to connect user:', error);
      throw error;
    }
  }

  /**
   * Disconnect current user from Stream Chat
   */
  public async disconnectUser(): Promise<void> {
    if (!this.client || !this.currentUser) {
      console.log('[StreamChatService] No user to disconnect');
      return;
    }

    try {
      await this.client.disconnectUser();
      this.currentUser = null;
      console.log('[StreamChatService] User disconnected');
    } catch (error) {
      console.error('[StreamChatService] Failed to disconnect user:', error);
      throw error;
    }
  }

  /**
   * Get the Stream Chat client instance
   * @throws Error if client is not initialized
   */
  public getClient(): StreamChat {
    if (!this.client) {
      throw new Error('Stream Chat client not initialized. Call connectUser first.');
    }
    return this.client;
  }

  /**
   * Get current connected user
   */
  public getCurrentUser(): OwnUserResponse | null {
    return this.currentUser;
  }

  /**
   * Check if user is connected
   */
  public isConnected(): boolean {
    return !!this.currentUser && !!this.client;
  }

  /**
   * Update user data
   * @param userData - Partial user data to update
   */
  public async updateUser(userData: Partial<StreamChatUser>): Promise<UserResponse> {
    const client = this.getClient();
    const userId = this.currentUser?.id;

    if (!userId) {
      throw new Error('No user connected');
    }

    try {
      const response = await client.upsertUser({
        id: userId,
        ...userData,
      });

      console.log('[StreamChatService] User updated:', userId);
      return response.users[userId];
    } catch (error) {
      console.error('[StreamChatService] Failed to update user:', error);
      throw error;
    }
  }

  /**
   * Handle token refresh
   * @param newToken - New JWT token
   */
  public async refreshToken(newToken: string): Promise<void> {
    console.log('[StreamChatService] Token refresh requested');

    // For development with disabled auth, we don't need to refresh tokens
    // In production, you would need to reconnect the user with the new token
    try {
      // Reconnect with new token
      await this.disconnectUser();
      await this.connectUser(this.currentUser!.id, newToken, {
        name: this.currentUser!.name,
        image: this.currentUser!.image,
      });
      console.log('[StreamChatService] Token refreshed via reconnection');
    } catch (error) {
      console.error('[StreamChatService] Failed to refresh token:', error);
      throw error;
    }
  }

  /**
   * Clean up resources
   */
  public async cleanup(): Promise<void> {
    await this.disconnectUser();
    this.client = null;
    this.currentUser = null;
    this.connectionPromise = null;
  }
}

export const streamChatService = StreamChatService.getInstance();
